import { useEffect, useState } from "react";
import { getObjectHistory } from "../services/api";
import { formatDate } from "../utils";

const ObjectHistory = ({ authToken, objectType, objectId, compact = false }) => {
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [visibleItems, setVisibleItems] = useState(5); // New state for visible items count

  useEffect(() => {
    const fetchHistory = async () => {
      setIsLoading(true);
      try {
        const response = await getObjectHistory(
          authToken,
          objectType,
          objectId
        );
        setHistory(response);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchHistory();
  }, [authToken, objectType, objectId]);

  const filteredHistory = history.filter(
    (item) =>
      (filter === "All" || item.activity_type === filter) &&
      (item.details.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.performed_by.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const displayedHistory = filteredHistory.slice(0, visibleItems);
  const hasMoreItems = filteredHistory.length > visibleItems;

  const handleLoadMore = () => {
    setVisibleItems(prev => prev + 5); // Load 5 more items
  };

  if (isLoading) return <p className="text-gray-500">Loading history...</p>;
  if (error) return <p className="text-red-500">Error: {error}</p>;

  /**
   * Sidecart rendering: a timeline, not a table.
   *
   * The full-page view has room for four columns, a type filter and a search
   * box. The sidecart card is about 660px wide, so the same table scrolls
   * sideways and the page chrome around it reads as a second page dropped
   * inside a card. History here is reference material you skim, so it gets
   * one line per entry and nothing else.
   */
  if (compact) {
    if (displayedHistory.length === 0) {
      return <p className="text-sm text-text-secondary">No history yet.</p>;
    }

    return (
      <>
        <ol className="divide-y divide-border">
          {displayedHistory.map((item) => (
            <li key={item.id} className="flex gap-2.5 py-2.5 first:pt-0">
              <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-slate-300" />
              <div className="min-w-0 flex-1">
                <p className="text-sm leading-snug text-text-primary">
                  {item.details}
                </p>
                <p className="mt-0.5 text-xs text-text-secondary">
                  {item.activity_type} · {item.performed_by} ·{" "}
                  {formatDate(item.timestamp, "short")}
                </p>
              </div>
            </li>
          ))}
        </ol>
        {hasMoreItems && (
          <button
            type="button"
            onClick={handleLoadMore}
            className="mt-2 w-full rounded py-2 text-sm font-medium text-primary transition-colors hover:bg-accent-teal-light-bg"
          >
            Load more
          </button>
        )}
      </>
    );
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 border-b border-border pb-4">
        <h2 className="text-xl font-bold text-gray-800 flex items-center space-x-2 mb-4 sm:mb-0">
          <span>History</span>
        </h2>
        <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4">
          <select
            className="border border-text-primary rounded py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 hover:border-teal-600 transition duration-300"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option>All</option>
            <option>Note</option>
            <option>Task</option>
            <option>Call</option>
          </select>
          <div className="relative">
            <input
              type="text"
              placeholder="Search history..."
              className="border border-text-primary rounded py-2 pl-10 pr-3 focus:ring-2 focus:ring-text-primary focus:ring-offset-2"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-primary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-sm rounded-lg hover:border hover:border-teal-200 transition duration-300 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-accent-teal-light-bg text-teal-800 uppercase text-sm font-semibold tracking-wider">
            <tr>
              <th className="px-6 py-3 text-left">Timestamp</th>
              <th className="px-6 py-3 text-left">Activity Type</th>
              <th className="px-6 py-3 text-left">Performed By</th>
              <th className="px-6 py-3 text-left">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {displayedHistory.length > 0 ? (
              displayedHistory.map((item) => (
                <tr
                  key={item.id}
                  className="even:bg-background hover:bg-accent-teal-light-bg transition duration-300"
                >
                  <td className="px-6 py-4 whitespace-normal">
                    <div className="text-sm text-gray-700">
                      {new Date(item.timestamp).toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-normal">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        item.activity_type.toLowerCase() === "note"
                          ? "bg-accent-teal-subtle text-teal-800"
                          : item.activity_type.toLowerCase() === "task"
                            ? "bg-accent-teal-subtle text-blue-800"
                            : item.activity_type.toLowerCase() === "call"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-green-100 text-green-800"
                      }`}
                    >
                      {item.activity_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-normal">
                    <div className="text-sm text-gray-700">
                      {item.performed_by}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-normal">
                    <div className="text-sm text-gray-700">{item.details}</div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={4}
                  className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-center"
                >
                  No results found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {hasMoreItems && (
        <div className="mt-4 text-center">
          <button
            onClick={handleLoadMore}
            className="inline-flex items-center px-4 py-2 border border-text-primary text-primary rounded-lg hover:bg-text-primary hover:text-white focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300"
          >
            <svg
              className="h-5 w-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
            Load More
          </button>
        </div>
      )}
    </div>
  );

};

export default ObjectHistory;